import type { ThemeColorName } from '@examdojo/styles';

// =============================================================================
// Block Definitions
// =============================================================================

/** A value I/O block — editable input or readonly output */
export interface FunctionMachineValueBlock {
  readonly type: 'value';
  readonly id: string;
  /** Pre-filled value. If undefined, the user must type it (when editable). */
  readonly value?: number;
  /** Whether the user can edit this value. Defaults to true for the first value block, false otherwise. */
  readonly editable?: boolean;
  /** Label override (e.g., "x", "f(x)"). First block defaults to "Input", last to "Output". */
  readonly label?: string;
  /** Whether to show the label above the block. Defaults to true for first and last value blocks. */
  readonly showLabel?: boolean;
  /** Pre-filled value when inverse mode is active */
  readonly inverseValue?: number;
  /** Label when inverse mode is active */
  readonly inverseLabel?: string;
  /** Editability when inverse mode is active */
  readonly inverseEditable?: boolean;
}

/** A function block that evaluates a math expression */
export interface FunctionMachineFunctionBlock {
  readonly type: 'function';
  readonly id: string;
  /** Math expression string, e.g. "2 * x + 1". Uses compileExpression format. */
  readonly expression: string;
  /** Variable name in the expression. Defaults to 'x'. */
  readonly variable?: string;
  /** Theme color for badge styling and arrow animation */
  readonly color: ThemeColorName;
  /** Display name, e.g. "f" → renders as "f(x) = 2x + 1" */
  readonly name?: string;
  /** Expression used in inverse mode, e.g. "(x - 1) / 2" */
  readonly inverseExpression?: string;
  /** Display name in inverse mode, e.g. "f⁻¹" */
  readonly inverseName?: string;
  /** Theme color in inverse mode */
  readonly inverseColor?: ThemeColorName;
}

/** A simple transformation block showing a single operation */
export interface FunctionMachineTransformBlock {
  readonly type: 'transform';
  readonly id: string;
  /** Display text for the operation, e.g. "+2", "×3", "÷4" */
  readonly operation: string;
  /** Evaluatable expression, e.g. "x + 2", "x * 3" */
  readonly expression: string;
  /** Variable name. Defaults to 'x'. */
  readonly variable?: string;
  /** Theme color. When set, uses a filled/dominant style (color-500 bg, white text). */
  readonly color?: ThemeColorName;
  /** Display text in inverse mode, e.g. "−2" */
  readonly inverseOperation?: string;
  /** Evaluatable expression in inverse mode, e.g. "x - 2" */
  readonly inverseExpression?: string;
  /** Theme color in inverse mode */
  readonly inverseColor?: ThemeColorName;
}

export type FunctionMachineBlock =
  | FunctionMachineValueBlock
  | FunctionMachineFunctionBlock
  | FunctionMachineTransformBlock;

// =============================================================================
// Config (Params / Display / Behavior)
// =============================================================================

export interface FunctionMachineParams {
  /** Ordered chain of blocks from left to right (or top to bottom in vertical layout) */
  readonly blocks: FunctionMachineBlock[];
  /** If true, reverses the visual direction of arrows (for inverse function machines) */
  readonly inverse?: boolean;
}

export interface FunctionMachineDisplayConfig {
  /** Layout direction. Defaults to 'horizontal'. */
  readonly layout?: 'horizontal' | 'vertical';
  /** Whether to show run button(s). Defaults to true. */
  readonly showRunButton?: boolean;
  /** 'global' = one Run button for entire chain. 'per-block' = Run button per function/transform. Defaults to 'global'. */
  readonly runButtonMode?: 'global' | 'per-block';
  /** When true, hides intermediate value blocks. Only first input and final output are shown. Defaults to false. */
  readonly compact?: boolean;
  /** Decimal precision for displayed numeric values. Defaults to 4. */
  readonly precision?: number;
}

export interface FunctionMachineBehaviorConfig {
  /** Auto-evaluate when input changes. Defaults to false. */
  readonly autoRun?: boolean;
  /** Arrow cascade animation duration in ms. Defaults to 600. */
  readonly animationDuration?: number;
}

export interface FunctionMachineConfig {
  readonly params: FunctionMachineParams;
  readonly display?: FunctionMachineDisplayConfig;
  readonly behavior?: FunctionMachineBehaviorConfig;
}

export type RunningState = 'idle' | 'running' | 'complete';

// =============================================================================
// Transform auto-inversion
// =============================================================================

/**
 * Derives the inverse expression and operation display text for a transform block.
 * Supports basic arithmetic: x + N, x - N, x * N, N * x, x / N.
 * Returns null if the expression pattern is not recognized.
 */
export function deriveTransformInverse(
  expression: string,
  variable = 'x',
): { expression: string; operation: string } | null {
  const e = expression.trim();
  const v = variable.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  let m: RegExpMatchArray | null;

  if ((m = e.match(new RegExp(`^${v}\\s*\\+\\s*(.+)$`)))) {
    return { expression: `${variable} - ${m[1].trim()}`, operation: `−${m[1].trim()}` };
  }
  if ((m = e.match(new RegExp(`^${v}\\s*-\\s*(.+)$`)))) {
    return { expression: `${variable} + ${m[1].trim()}`, operation: `+${m[1].trim()}` };
  }
  if ((m = e.match(new RegExp(`^${v}\\s*\\*\\s*(.+)$`)))) {
    return { expression: `${variable} / ${m[1].trim()}`, operation: `÷${m[1].trim()}` };
  }
  if ((m = e.match(new RegExp(`^(.+?)\\s*\\*\\s*${v}$`)))) {
    return { expression: `${variable} / ${m[1].trim()}`, operation: `÷${m[1].trim()}` };
  }
  if ((m = e.match(new RegExp(`^${v}\\s*/\\s*(.+)$`)))) {
    return { expression: `${variable} * ${m[1].trim()}`, operation: `×${m[1].trim()}` };
  }

  return null;
}
